import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockLotsKeys } from '../stock-lots/queries';
import { stockMovementsApi } from './service';
import type {
  AdjustmentValues,
  ConsumptionValues,
  ReceiptValues,
  TransferValues,
} from './schema';

/**
 * Query key factory for stock-movements (one list per stockLotId).
 *
 *   stockMovementsKeys.forLot(id, limit?)   list of movements for one lot
 *
 * Mutations (receipt/consumption/adjustment/transfer) invalidate
 * BOTH this key AND the parent stockLotsKeys.detail(id) since the
 * lot's quantityOnHand also changes. For transfer, BOTH source AND
 * destination lots need invalidation.
 */
export const stockMovementsKeys = {
  all: ['stock-movements'] as const,
  forLot: (stockLotId: string, limit?: number) =>
    [...stockMovementsKeys.all, 'for-lot', stockLotId, limit ?? null] as const,
};

export function useStockMovements(
  stockLotId: string | undefined,
  limit?: number,
) {
  return useQuery({
    queryKey: stockLotId
      ? stockMovementsKeys.forLot(stockLotId, limit)
      : stockMovementsKeys.all,
    queryFn: () =>
      stockMovementsApi.listForLot(stockLotId as string, limit),
    enabled: Boolean(stockLotId),
  });
}

/**
 * Helper: invalidate a lot's detail + its movements list. Used by
 * receipt / consumption / adjustment hooks. Lists are also
 * invalidated globally so the lots-list page (with low-stock
 * highlighting) refreshes too.
 */
function invalidateLotAndMovements(
  qc: ReturnType<typeof useQueryClient>,
  lotId: string,
) {
  qc.invalidateQueries({ queryKey: stockLotsKeys.detail(lotId) });
  qc.invalidateQueries({ queryKey: stockLotsKeys.lists() });
  qc.invalidateQueries({
    queryKey: [...stockMovementsKeys.all, 'for-lot', lotId],
  });
}

export function useReceiptMovement(stockLotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceiptValues) =>
      stockMovementsApi.receipt(stockLotId, input),
    onSuccess: () => invalidateLotAndMovements(qc, stockLotId),
  });
}

export function useConsumptionMovement(stockLotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConsumptionValues) =>
      stockMovementsApi.consumption(stockLotId, input),
    onSuccess: () => invalidateLotAndMovements(qc, stockLotId),
  });
}

export function useAdjustmentMovement(stockLotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdjustmentValues) =>
      stockMovementsApi.adjustment(stockLotId, input),
    onSuccess: () => invalidateLotAndMovements(qc, stockLotId),
  });
}

/**
 * Transfer hook — invalidates BOTH source AND destination lots
 * (both their details AND both their movements lists) since the
 * backend atomically creates two movements (transfer_out on source,
 * transfer_in on dest) inside the same transaction.
 */
export function useTransferMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TransferValues) =>
      stockMovementsApi.transfer(input),
    onSuccess: (_movement, vars) => {
      invalidateLotAndMovements(qc, vars.sourceLotId);
      invalidateLotAndMovements(qc, vars.destLotId);
    },
  });
}
